import {
  letters,
  lettersTransform,
  markColours,
  markViewBox,
  markViewBoxLong,
  tagBody,
  tagFrame,
  tagHole,
  tagStringLong,
  tagStringShort,
  tagStrokeWidth,
  tagTilt,
} from '@core/brand/mark';

/** The white tag itself, the phone's `TagMark`: only the string and what shows through the hole vary. */
function WhiteTag({
  string,
  stringWidth,
  hole,
}: {
  string: string;
  stringWidth: number;
  hole: string;
}) {
  return (
    <g transform={tagTilt}>
      <path
        d={string}
        fill="none"
        stroke="#ffffff"
        strokeWidth={stringWidth}
        strokeLinecap="round"
      />
      <path
        d={tagBody}
        fill="#ffffff"
        stroke="#ffffff"
        strokeWidth={tagStrokeWidth}
        strokeLinejoin="round"
      />
      <path
        d={tagFrame}
        fill="none"
        stroke={markColours.deep}
        strokeOpacity={0.28}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <circle
        cx={tagHole.cx}
        cy={tagHole.cy}
        r={tagHole.ring}
        fill="none"
        stroke={markColours.deep}
        strokeOpacity={0.35}
        strokeWidth={1.6}
      />
      <circle cx={tagHole.cx} cy={tagHole.cy} r={tagHole.r} style={{ fill: hole }} />
      <path d={letters} transform={lettersTransform} fill={markColours.deep} />
    </g>
  );
}

/**
 * The AttireRent mark on its tile: the white swing tag with AR, on the theme's colour.
 *
 * <p>
 * The tile is `bg-primary` by default, so the mark follows the person's theme like every other
 * accent, and the hole shows the tile through it. The tag is the phone's, ink letters included. A
 * caller with a fixed colour (the sign-in page) passes `tone`. The shapes come from
 * `@core/brand/mark`, the same ones the app icon files are drawn from.
 * </p>
 */
export function BrandMark({
  size = 28,
  tone,
  radius,
  className = '',
}: {
  size?: number;
  /** A fixed tile colour; the theme's when absent. */
  tone?: string;
  /** The tile's corner, for a tile drawn large; the template's small corner when absent. */
  radius?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center rounded-md ${tone ? '' : 'bg-primary'} ${className}`}
      style={{
        width: size,
        height: size,
        ...(tone ? { background: tone } : {}),
        ...(radius === undefined ? {} : { borderRadius: radius }),
      }}
    >
      <svg viewBox={markViewBox} width={size * 0.62} height={size * 0.9}>
        <WhiteTag string={tagStringShort} stringWidth={2.2} hole={tone ?? 'var(--color-primary)'} />
      </svg>
    </span>
  );
}

/**
 * The white tag on its long string, for a hero: place it at the very top of the band and the string
 * runs up out of view, as on the phone's sign-in. The hole shows the band.
 */
export function HangingTag({ width }: { width: number }) {
  const [, , boxWidth, boxHeight] = markViewBoxLong.split(' ').map(Number) as [
    number,
    number,
    number,
    number,
  ];

  return (
    <svg
      aria-hidden="true"
      viewBox={markViewBoxLong}
      width={width}
      height={(width * boxHeight) / boxWidth}
      className="shrink-0"
    >
      <WhiteTag string={tagStringLong} stringWidth={1.6} hole="var(--color-hero)" />
    </svg>
  );
}
